import { USER_ROLES_ENUM } from "@/utils/constants/user";

const PERMISSION_CATEGORY = {
  admin: "admin",
  countersAndRisk: "countersAndRisk",
  maintenance: "maintenance",
  observer: "observer",
  pilot: "pilot",
} as const;

type PermissionCategory =
  (typeof PERMISSION_CATEGORY)[keyof typeof PERMISSION_CATEGORY];

const ROLE_RANK: Record<USER_ROLES_ENUM, number> = {
  [USER_ROLES_ENUM.OBS]: 0,
  [USER_ROLES_ENUM.CREW_CHIEF]: 1,
  [USER_ROLES_ENUM.PILOT]: 2,
  [USER_ROLES_ENUM.MX]: 3,
  [USER_ROLES_ENUM.ADMIN]: 4,
};

/** Each category maps to a role tier on the ladder. */
const CATEGORY_MIN_RANK: Record<PermissionCategory, number> = {
  [PERMISSION_CATEGORY.observer]: ROLE_RANK[USER_ROLES_ENUM.OBS],
  [PERMISSION_CATEGORY.countersAndRisk]: ROLE_RANK[USER_ROLES_ENUM.CREW_CHIEF],
  [PERMISSION_CATEGORY.pilot]: ROLE_RANK[USER_ROLES_ENUM.PILOT],
  [PERMISSION_CATEGORY.maintenance]: ROLE_RANK[USER_ROLES_ENUM.MX],
  [PERMISSION_CATEGORY.admin]: ROLE_RANK[USER_ROLES_ENUM.ADMIN],
};

function normalizePermissions(permissions: string[]): Set<string> {
  return new Set(permissions.map((permission) => permission.toUpperCase()));
}

function getEffectiveRoleRank(permissions: string[]): number {
  const normalizedPermissions = normalizePermissions(permissions);
  let highestRank = ROLE_RANK[USER_ROLES_ENUM.OBS];

  for (const role of Object.values(USER_ROLES_ENUM)) {
    if (normalizedPermissions.has(role)) {
      highestRank = Math.max(highestRank, ROLE_RANK[role]);
    }
  }

  return highestRank;
}

function canAccessPermissionCategory(
  permissions: string[],
  category: PermissionCategory,
): boolean {
  return getEffectiveRoleRank(permissions) >= CATEGORY_MIN_RANK[category];
}

const canAccess = {
  admin: (permissions: string[]) =>
    canAccessPermissionCategory(permissions, PERMISSION_CATEGORY.admin),
  countersAndRisk: (permissions: string[]) =>
    canAccessPermissionCategory(
      permissions,
      PERMISSION_CATEGORY.countersAndRisk,
    ),
  maintenance: (permissions: string[]) =>
    canAccessPermissionCategory(permissions, PERMISSION_CATEGORY.maintenance),
  observer: (permissions: string[]) =>
    canAccessPermissionCategory(permissions, PERMISSION_CATEGORY.observer),
  pilot: (permissions: string[]) =>
    canAccessPermissionCategory(permissions, PERMISSION_CATEGORY.pilot),
} as const satisfies Record<
  PermissionCategory,
  (permissions: string[]) => boolean
>;

/** ADMIN permission rank, or organization owner (isRootAccount). */
function hasAdminOrHigherAccess(user: {
  PermissionsData: string[];
  isRootAccount?: boolean;
}): boolean {
  return Boolean(user.isRootAccount) || canAccess.admin(user.PermissionsData);
}

function userMeetsRequiredRoles(
  user: { PermissionsData: string[]; isRootAccount?: boolean },
  requiredRoles: readonly string[],
): boolean {
  const normalizedRequired = requiredRoles.map((role) => role.toUpperCase());
  if (
    normalizedRequired.includes(USER_ROLES_ENUM.ADMIN) &&
    hasAdminOrHigherAccess(user)
  ) {
    return true;
  }

  const held = normalizePermissions(user.PermissionsData);
  return normalizedRequired.some((role) => held.has(role));
}

/**
 * Role catalog visibility (orthogonal to countersAndRisk write gate). Mirrors
 * `canAccessMfratFormAudience` in my-fire-plane-core — keep both in step.
 * - empty audience list → any user
 * - otherwise the viewer must hold one of the named roles; outranking them is
 *   not enough, because a role-specific form is written for that job
 * - ADMIN and organization owners bypass audience targeting
 */
function canAccessMfratFormAudience(
  permissions: string[],
  audienceRoles: readonly string[] | null | undefined,
  bypassAudience = false,
): boolean {
  const audience = (audienceRoles ?? []).map((role) => role.toUpperCase());
  if (audience.length === 0 || bypassAudience) {
    return true;
  }

  if (getEffectiveRoleRank(permissions) >= ROLE_RANK[USER_ROLES_ENUM.ADMIN]) {
    return true;
  }

  const held = normalizePermissions(permissions);
  return audience.some((role) => held.has(role));
}

export {
  canAccess,
  canAccessMfratFormAudience,
  canAccessPermissionCategory,
  CATEGORY_MIN_RANK,
  getEffectiveRoleRank,
  hasAdminOrHigherAccess,
  PERMISSION_CATEGORY,
  ROLE_RANK,
  userMeetsRequiredRoles,
};
export type { PermissionCategory };
